import { useId } from "react";
import type { ReactNode } from "react";
import {
  BOARD,
  GO_SALARY,
  GROUP_INFO,
  JAIL_FINE,
  MAX_JAIL_ATTEMPTS,
  RAILROAD_FIELDS,
  UTILITY_FIELDS,
} from "@grundbesitz/shared";
import type { ColorGroup, Field, OwnableField, PlayerState, PublicGameState, RoomMember } from "@grundbesitz/shared";
import { computeRent, ownsGroup, unmortgageCost } from "@grundbesitz/engine";
import { formatMoney } from "../../lib/format";
import { pickInk } from "../../lib/boardLayout";
import { buildingsText } from "./fieldLabel";
import { FieldGlyph, glyphFor } from "./FieldGlyph";
import { GroupBadge, GroupPatternDef } from "./GroupBadge";
import { TokenIcon } from "./TokenIcon";
import "./board.css";

interface RentRow {
  label: string;
  value: string;
}

function GroupBand({ group }: { group: ColorGroup }) {
  const id = `gb-card-${useId().replace(/[^a-zA-Z0-9]/g, "")}`;
  const color = GROUP_INFO[group].color;
  return (
    <div className="relative flex h-14 items-center px-4" style={{ color: pickInk(color) }}>
      <svg className="absolute inset-0 h-full w-full" aria-hidden="true" focusable="false">
        <defs>
          <GroupPatternDef id={id} group={group} />
        </defs>
        <rect width="100%" height="100%" fill={`url(#${id})`} />
      </svg>
      <span className="relative">
        <GroupBadge group={group} />
      </span>
    </div>
  );
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="flex flex-col gap-1.5">
      <h4 className="font-display text-sm font-semibold text-ink-2">{title}</h4>
      {children}
    </section>
  );
}

function RentTable({ rows, active, note }: { rows: RentRow[]; active: number | null; note?: string }) {
  return (
    <Section title="Miete">
      <ul className="m-0 flex list-none flex-col gap-0.5 p-0 text-sm">
        {rows.map((row, i) => (
          <li key={row.label} className="gb-rent-row" data-active={active === i} aria-current={active === i ? "true" : undefined}>
            <span>{row.label}</span>
            <span>{row.value}</span>
          </li>
        ))}
      </ul>
      {note ? <p className="m-0 px-2.5 text-xs text-ink-2">{note}</p> : null}
    </Section>
  );
}

function Facts({ rows }: { rows: RentRow[] }) {
  return (
    <dl className="m-0 grid grid-cols-[1fr_auto] gap-x-4 gap-y-1 text-sm">
      {rows.map((row) => (
        <div key={row.label} className="col-span-2 grid grid-cols-subgrid">
          <dt className="text-ink-2">{row.label}</dt>
          <dd className="m-0 font-semibold tabular-nums">{row.value}</dd>
        </div>
      ))}
    </dl>
  );
}

function ownerOf(game: PublicGameState, members: RoomMember[], id: string): { name: string; player: PlayerState | undefined } {
  const player = game.players.find((p) => p.id === id);
  return { name: player?.name ?? members.find((m) => m.id === id)?.name ?? "Unbekannt", player };
}

function OwnerBlock({ field, game, members }: { field: OwnableField; game: PublicGameState; members: RoomMember[] }) {
  const prop = game.properties[field.index];
  if (!prop?.owner) {
    return (
      <Section title="Besitzer">
        <p className="m-0 text-sm">Frei. Das Grundstück gehört noch der Bank.</p>
      </Section>
    );
  }
  const { name, player } = ownerOf(game, members, prop.owner);
  const built = buildingsText(prop.buildings);
  const completeGroup = field.kind === "STREET" && ownsGroup(game, prop.owner, field.group);
  return (
    <Section title="Besitzer">
      <div className="flex items-center gap-3">
        {player ? (
          <span className="grid size-11 shrink-0 place-items-center rounded-full border border-line bg-surface-2">
            <TokenIcon token={player.token} color={player.color} size={34} />
          </span>
        ) : null}
        <div className="min-w-0">
          <p className="m-0 truncate text-base font-semibold">{name}</p>
          <ul className="m-0 flex list-none flex-wrap gap-1.5 p-0 pt-1 text-xs font-medium">
            {prop.mortgaged ? (
              <li className="inline-flex items-center gap-1 rounded-full border border-danger px-2 py-0.5 text-danger">
                <span aria-hidden="true" className="grid size-4 place-items-center rounded-full bg-danger text-[10px] font-bold text-surface">
                  H
                </span>
                Belastet, keine Miete
              </li>
            ) : null}
            {built ? <li className="rounded-full border border-line px-2 py-0.5">{built}</li> : null}
            {completeGroup ? <li className="rounded-full border border-line px-2 py-0.5">Ganze Gruppe</li> : null}
          </ul>
        </div>
      </div>
    </Section>
  );
}

function ownedCount(game: PublicGameState, owner: string, fields: readonly number[]): number {
  return fields.filter((i) => game.properties[i]?.owner === owner).length;
}

function PlayersHere({ index, game }: { index: number; game: PublicGameState }) {
  const here = game.players.filter((p) => !p.eliminated && p.position === index);
  if (here.length === 0) return null;
  const jail = index === 10;
  return (
    <Section title={jail ? "Im Gefängnis und zu Besuch" : "Figuren auf diesem Feld"}>
      <ul className="m-0 flex list-none flex-wrap gap-2 p-0 text-sm">
        {here.map((p) => (
          <li key={p.id} className="inline-flex items-center gap-1.5 rounded-full border border-line py-0.5 pl-1 pr-2.5">
            <TokenIcon token={p.token} color={p.color} size={24} />
            {p.name}
            {jail ? <span className="text-ink-2">{p.inJail ? "(hinter Gitter)" : "(zu Besuch)"}</span> : null}
          </li>
        ))}
      </ul>
    </Section>
  );
}

function OwnableCard({ field, game, members }: { field: OwnableField; game: PublicGameState; members: RoomMember[] }) {
  const prop = game.properties[field.index];
  const owner = prop?.owner ?? null;
  const live = owner !== null && !prop.mortgaged;
  const diceTotal = game.turn.lastRoll ? game.turn.lastRoll[0] + game.turn.lastRoll[1] : 0;
  const rent = live ? computeRent(game, field.index, diceTotal) : 0;

  let rows: RentRow[];
  let active: number | null = null;
  let note: string | undefined;
  if (field.kind === "STREET") {
    rows = [
      { label: "Grundmiete", value: formatMoney(field.rents[0]) },
      { label: "Mit allen Straßen der Gruppe", value: formatMoney(field.rents[0] * 2) },
      { label: "Mit 1 Haus", value: formatMoney(field.rents[1]) },
      { label: "Mit 2 Häusern", value: formatMoney(field.rents[2]) },
      { label: "Mit 3 Häusern", value: formatMoney(field.rents[3]) },
      { label: "Mit 4 Häusern", value: formatMoney(field.rents[4]) },
      { label: "Mit Hotel", value: formatMoney(field.rents[5]) },
    ];
    if (live) active = prop.buildings > 0 ? 1 + prop.buildings : ownsGroup(game, owner, field.group) ? 1 : 0;
  } else if (field.kind === "RAILROAD") {
    rows = field.rents.map((r, i) => ({ label: i === 0 ? "1 Bahnhof" : `${i + 1} Bahnhöfe`, value: formatMoney(r) }));
    if (live) active = ownedCount(game, owner, RAILROAD_FIELDS) - 1;
  } else {
    rows = [
      { label: "Ein Werk", value: `Würfelsumme × ${field.multipliers[0]}` },
      { label: "Beide Werke", value: `Würfelsumme × ${field.multipliers[1]}` },
    ];
    if (live) active = ownedCount(game, owner, UTILITY_FIELDS) - 1;
    note = "Die Miete ergibt sich aus der Würfelsumme des Spielers, der hier landet.";
  }

  const facts: RentRow[] = [{ label: "Kaufpreis", value: formatMoney(field.price) }];
  if (field.kind === "STREET") {
    facts.push({ label: "Haus (je Haus)", value: formatMoney(field.houseCost) });
    facts.push({ label: "Hotel (nach 4 Häusern)", value: formatMoney(field.houseCost) });
  }
  facts.push({ label: "Hypothekenwert", value: formatMoney(field.mortgage) });
  facts.push({ label: "Hypothek auflösen (Wert + 10 %)", value: formatMoney(unmortgageCost(field.index)) });

  return (
    <>
      <RentTable rows={rows} active={active} note={note} />
      {live && rent > 0 && field.kind !== "UTILITY" ? (
        <p className="m-0 text-sm font-semibold">Aktuelle Miete: {formatMoney(rent)}</p>
      ) : null}
      <Section title="Preise">
        <Facts rows={facts} />
      </Section>
      <OwnerBlock field={field} game={game} members={members} />
    </>
  );
}

function StatementCard({ field, game }: { field: Exclude<Field, OwnableField>; game: PublicGameState }) {
  const pot = game.options.houseRules.freeParkingPot;
  let lines: string[];
  switch (field.kind) {
    case "GO":
      lines = [
        `Wer LOS erreicht oder überquert, erhält ${formatMoney(GO_SALARY)} von der Bank.`,
        ...(game.options.houseRules.doubleGoOnLanding ? [`Bei genauer Landung auf LOS gibt es ${formatMoney(GO_SALARY * 2)}.`] : []),
      ];
      break;
    case "JAIL":
      lines = [
        "Wer hier landet, ist nur zu Besuch. Es passiert nichts.",
        `Wer verhaftet wird, sitzt hinter Gitter. Raus kommt man mit einem Pasch (bis zu ${MAX_JAIL_ATTEMPTS} Versuche), gegen ${formatMoney(JAIL_FINE)} Strafe oder mit einer Freikarte. Miete kassieren, bauen und handeln geht weiter.`,
      ];
      break;
    case "FREE_PARKING":
      lines = pot
        ? [`Steuern und Strafen wandern in den Topf. Wer hier landet, erhält den Topf. Aktuell: ${formatMoney(game.freeParkingPot)}.`]
        : ["Zum Ausruhen. Hier passiert nichts."];
      break;
    case "GO_TO_JAIL":
      lines = ["Wer hier landet, geht sofort ins Gefängnis, ohne LOS zu überqueren und ohne M zu kassieren."];
      break;
    case "TAX":
      lines = [
        `Zahle ${formatMoney(field.amount)} ${pot ? "in den Frei-Parken-Topf" : "an die Bank"}.`,
        "Reicht das Geld nicht, müssen Häuser verkauft oder Grundstücke belastet werden.",
      ];
      break;
    case "CHANCE":
      lines = [`Ziehe die oberste Ereigniskarte und führe sie aus. ${game.deckSizes.chance} Karten im Stapel.`];
      break;
    case "COMMUNITY":
      lines = [`Ziehe die oberste Gemeinschaftskarte und führe sie aus. ${game.deckSizes.community} Karten im Stapel.`];
      break;
  }
  return (
    <>
      {lines.map((line) => (
        <p key={line} className="m-0 text-sm leading-relaxed">
          {line}
        </p>
      ))}
      <PlayersHere index={field.index} game={game} />
    </>
  );
}

/** "Besitzrechtkarte": every value of a field and its current owner. Pure content, the caller provides the dialog. */
export function PropertyCard({ field: index, game, members }: { field: number; game: PublicGameState; members: RoomMember[] }) {
  const field = BOARD[index];
  if (!field) return null;
  const glyph = glyphFor(field);
  const ownable = field.kind === "STREET" || field.kind === "RAILROAD" || field.kind === "UTILITY";
  const kindLabel =
    field.kind === "RAILROAD" ? "Bahnhof" : field.kind === "UTILITY" ? "Werk" : field.kind === "TAX" ? "Steuer" : field.kind === "CHANCE" || field.kind === "COMMUNITY" ? "Karten" : null;

  return (
    <article className="overflow-hidden rounded-2xl border border-line bg-surface text-ink" aria-label={`Besitzrechtkarte: ${field.name}`}>
      {field.kind === "STREET" ? (
        <GroupBand group={field.group} />
      ) : (
        <div className="flex h-14 items-center gap-2 bg-accent-soft px-4">
          {glyph ? <FieldGlyph kind={glyph} size={36} /> : null}
          {kindLabel ? <span className="text-sm font-semibold text-ink">{kindLabel}</span> : null}
        </div>
      )}
      <div className="flex flex-col gap-4 p-4">
        <h3 className="m-0 font-display text-2xl font-bold leading-tight">{field.name}</h3>
        {ownable ? <OwnableCard field={field as OwnableField} game={game} members={members} /> : <StatementCard field={field as Exclude<Field, OwnableField>} game={game} />}
      </div>
    </article>
  );
}
